import { RadioGroup, RadioGroupItem } from "@/components/podkit/forms/RadioListField";
import { Text } from "@/components/podkit/typography/Text";
import { useAtomValue, useSetAtom, type Atom, type WritableAtom } from "jotai";
import type {
    WorkflowEditFormStepData,
    WorkflowEditFormStepType,
} from "@/components/automations/automation-edit-form-data";
import type { FC } from "react";
import { Button } from "@/components/flexkit/Button";
import { IconConsoleSimple } from "central-icons/IconConsoleSimple";
import { IconBubbleText6 } from "central-icons/IconBubbleText6";
import { IconPullRequest } from "central-icons/IconPullRequest";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { ShellInput } from "@/components/flexkit/ShellInput";
import { AutomationPullRequestStepEditor } from "@/routes/automations/automation-edit-page/AutomationPullRequestStepEditor";

type AutomationEmptyStepCreatorProps = {
    currentEditingStepAtom: Atom<WorkflowEditFormStepData | null>;
    updateCurrentStepAtom: WritableAtom<null, [Partial<WorkflowEditFormStepData>], void>;
    addNewStepAtom: WritableAtom<null, [], void>;
};

/**
 * Displayed when automation details page is in edit mode and no steps are present.
 * Presents a UI for creating the initial step of the automation.
 */
export const AutomationEmptyStepCreator: FC<AutomationEmptyStepCreatorProps> = ({
    currentEditingStepAtom,
    updateCurrentStepAtom,
    addNewStepAtom,
}) => {
    const currentStep = useAtomValue(currentEditingStepAtom);
    const updateCurrentStep = useSetAtom(updateCurrentStepAtom);
    const addNewStep = useSetAtom(addNewStepAtom);

    if (!currentStep) {
        return null;
    }

    const handleTypeChange = (newType: WorkflowEditFormStepType) => {
        const currentType = currentStep.type;
        const canPreserveContent =
            (currentType === "prompt" || currentType === "command") &&
            (newType === "prompt" || newType === "command");

        if (canPreserveContent && (currentStep.type === "prompt" || currentStep.type === "command")) {
            updateCurrentStep({ ...currentStep, type: newType } as WorkflowEditFormStepData);
        } else {
            switch (newType) {
                case "command":
                case "prompt":
                    updateCurrentStep({ id: currentStep.id, type: newType, content: "" });
                    break;
                case "pr":
                    updateCurrentStep({
                        id: currentStep.id,
                        type: newType,
                        title: "",
                        description: "",
                        branch: "",
                        isDraft: false,
                    });
                    break;
                case "report":
                    updateCurrentStep({ id: currentStep.id, type: newType, outputs: [] });
                    break;
            }
        }
    };

    let contentEditor: React.ReactNode | null;
    switch (currentStep.type) {
        case "command":
            contentEditor = (
                <ShellInput
                    value={currentStep.content}
                    onChange={(content) => updateCurrentStep({ content })}
                    placeholder="Enter shell commands"
                    minHeight={120}
                    maxHeight={300}
                />
            );
            break;
        case "prompt":
            contentEditor = (
                <Textarea
                    value={currentStep.content}
                    onChange={(e) => updateCurrentStep({ content: e.target.value })}
                    placeholder="Enter the prompt for this step"
                    className="min-h-32 resize-none"
                />
            );
            break;
        case "pr":
            contentEditor = <AutomationPullRequestStepEditor step={currentStep} onChange={updateCurrentStep} />;
            break;
        case "report":
            // Report type not shown in empty step creator
            contentEditor = null;
            break;
    }

    return (
        <div className="mt-4 flex max-w-screen-lg flex-1 flex-col rounded-xl border border-border-base bg-surface-primary p-4 shadow-md">
            <Text className="text-md font-medium">No steps yet</Text>
            <Text className="text-content-strong">
                Define what Ona will do when this workflow runs. Add a command, prompt, or pull request action to get
                started.
            </Text>
            <StepTypeRadioGroup type={currentStep.type} onTypeChange={handleTypeChange} />
            {contentEditor}
            <div className="mt-3 flex justify-end gap-2">
                <Button variant="primary" size="sm" onClick={() => addNewStep()} data-tracking-id="add">
                    Add
                </Button>
            </div>
        </div>
    );
};

type StepTypeRadioGroupProps = {
    type: WorkflowEditFormStepType;
    onTypeChange: (type: WorkflowEditFormStepType) => void;
};

const StepTypeRadioGroup: FC<StepTypeRadioGroupProps> = ({ type, onTypeChange }) => {
    return (
        <RadioGroup
            value={type}
            onValueChange={(value) => onTypeChange(value as WorkflowEditFormStepType)}
            className="my-3 flex flex-row gap-4 text-base"
        >
            <label className="flex flex-row items-center gap-2">
                <RadioGroupItem value="command" />
                <span>
                    <IconConsoleSimple size={20} />
                </span>
                <span className="translate-y-px">Command</span>
            </label>
            <label className="flex flex-row items-center gap-2">
                <RadioGroupItem value="prompt" />
                <span>
                    <IconBubbleText6 size={20} />
                </span>
                <span className="translate-y-[0.5px]">Prompt</span>
            </label>
            <label className="flex flex-row items-center gap-2">
                <RadioGroupItem value="pr" />
                <span>
                    <IconPullRequest size={20} />
                </span>
                <span className="translate-y-px">Create Pull Request</span>
            </label>
        </RadioGroup>
    );
};
